import {patternMatch} from "./pattern_match";
import {
	BaseTree,
	BlockTree,
	CompilationUnitTree,
	IdentTree,
	NameTree,
	ReferenceTypeTree,
} from "./model";
import {
	AnalysisCtx,
	makeBlockScope,
	resolveBlockTree,
	resolveTypeTree,
} from "./analysis";
import {Token} from "./lexer";
import {CompletionResult} from "@codemirror/autocomplete";
import {Parser} from "./parser";
import {CompileError, JavaVersion} from "./error";
import {getFirstScopeOfChain} from "./hovers/utils";
import {CompletionPattern} from "./completions/factory";
import {EXPRESSIONS} from "./completions/expressions";
import {IMPORTS} from "./completions/imports";
import {NAMES} from "./completions/names";
import {FIELD_ACCESS} from "./completions/field_access";
import {FN_ARGS} from "./completions/fn_args";
import {EditorView} from "@codemirror/view";
import {PACKAGE_COMPLETIONS} from "./completions/package";
import {fillContext} from "./project";

function skipCharacter(view: EditorView, loc: number) {
	view.dispatch([
		view.state.update({
			changes: {from: loc - 1, to: loc, insert: ""},
			selection: {anchor: loc, head: loc},
		}),
	]);
}

function inferInsertionPoint(
	before: string,
	after: string,
	presumed: number
): {inserted: string; index: number} | undefined {
	if (before.length + 1 !== after.length) return undefined;

	for (let i = 0; i < presumed; i++) {
		if (before[i] !== after[i]) {
			return undefined;
		}
	}

	const inserted = after[presumed];
	return {inserted: inserted!, index: presumed};
}

export class CompletionEngine {
	patterns: CompletionPattern<any, (...args: any[]) => CompletionResult>[] = [];

	constructor() {
		this.registerPatterns(PACKAGE_COMPLETIONS);
		this.registerPatterns(IMPORTS);

		this.registerPatterns(EXPRESSIONS);
		this.registerPatterns(FN_ARGS);
		this.registerPatterns(FIELD_ACCESS);
		this.registerPatterns(NAMES);
	}

	registerPattern(pat: CompletionPattern<any, any>) {
		this.patterns.push(pat);
	}

	registerPatterns(pats: CompletionPattern<any, any>[]) {
		for (const pat of pats) {
			this.registerPattern(pat);
		}
	}

	/**
	 * Given the original text of the compilation unit and the parsed tree, as well as an offset into the text,
	 * find the chain of nodes that the completion engine should consider.
	 *
	 * This is slightly different at findChainAtPosition when the position is somewhere in a block. While typing a line
	 * of code, the intermediate result may be parsed in a way that is annoying to disentangle when writing completion
	 * strategies. For example, in the following situation:
	 *
	 * void f() {
	 *     Object o;
	 *     o.|
	 *     String s;
	 * }
	 *
	 * the code is syntactically valid and the cursor | is in the middle of a reference type o.String, rather than
	 * on the right-hand side of an invalid qualified identifier.
	 *
	 * In this case, this function looks for the innermost enclosing block and, if it exists, wipes out everything
	 * in that block following the cursor location. It then re-parses that block only, and substitutes it into the
	 * chain, with the same scope information as the original block. In other words, the following is parsed using
	 * parser.consumeBlock():
	 *
	 * {
	 *     Object o;
	 *     o.
	 *
	 * @param originalText The original text that was parsed.
	 * @param compilationUnit The compilation unit that resulted from parsing the original text. The CU should have all
	 *   scopes resolved.
	 * @param pos The position to request completions from.
	 * @param javaVersion The Java version to use
	 */
	findChainForCompletionPurposes(
		originalText: string,
		compilationUnit: CompilationUnitTree,
		pos: number,
		javaVersion: JavaVersion
	): (BaseTree | Token)[] | null {
		const chain = compilationUnit.findChainAtPosition(pos);
		if (!chain) return null;

		// Discover the lowest enclosing BlockTree which also has a resolved scope
		let innermostBlockI = chain.length - 1,
			originalBlock: BlockTree | null = null;
		while (innermostBlockI >= 0) {
			const entry = chain[innermostBlockI];
			if (entry instanceof BlockTree) {
				if (!entry.scope) {
					break;
				}
				originalBlock = entry;
				break;
			}
			innermostBlockI--;
		}

		if (originalBlock) {
			const blockTextUntilCompletionPos =
				" ".repeat(originalBlock.span.start) +
				originalText.slice(originalBlock.span.start, pos);

			const parser = new Parser(blockTextUntilCompletionPos, {
				forceFinalIdentifier: true,
			});
			const newBlock = parser.consumeBlock();
			const ignoreErrors: AnalysisCtx = {
				reportError: (err: CompileError) => {},
				javaVersion,
			};
			if (newBlock) {
				newBlock.resolveSpans();
				const surroundingScope = originalBlock.scope?.prev;
				if (surroundingScope) {
					const blockScope = makeBlockScope(surroundingScope);
					// in case the block scope in question is the first to be defined as static
					blockScope.isStatic = originalBlock.scope?.isStatic!;
					// By performing resolution using only the completed part of the chain, we omit variables which
					// are defined later in the block ...
					resolveBlockTree(blockScope, newBlock, ignoreErrors);
					// ... but we allow access to types defined later in the block.
					blockScope.types = originalBlock.scope!.types;
				}

				const postTrimmedChain = newBlock.findChainAtPosition(pos);
				if (postTrimmedChain) {
					chain.length = innermostBlockI; // remove up to block tree
					chain.push(...postTrimmedChain);
				}
			}
		}

		return chain;
	}

	completionsAt(
		originalText: string,
		compilationUnit: CompilationUnitTree,
		pos: number,
		javaVersion: JavaVersion = 8
	): CompletionResult | null {
		const chain = this.findChainForCompletionPurposes(
			originalText,
			compilationUnit,
			pos,
			javaVersion
		);
		if (!chain) return null;

		const scope = getFirstScopeOfChain(chain);
		if (!scope) return null;

		for (const pat of this.patterns) {
			const match = patternMatch(pat.pat, chain);
			if (match !== null) {
				const result = pat.handler(chain, scope, ...match);
				if (result !== null) {
					return result;
				}
			}
		}

		return null;
	}

	onTypedLt(
		doc: string,
		before: CompilationUnitTree,
		after: CompilationUnitTree,
		loc: number,
		view: EditorView
	) {
		// Just typed a <; if we are on a reference type with type arguments we should insert a > to the right
		// of the cursor

		//          V look here
		// ArrayList|<
		const chain = after.findChainAtPosition(loc - 1);
		if (!chain) return;

		const scope = getFirstScopeOfChain(chain!);
		if (!scope) return;

		const lastIdentOrName = chain.filter(
			(c) => c instanceof NameTree || c instanceof IdentTree
		)[0] as NameTree | IdentTree | undefined;

		if (!lastIdentOrName) return;

		const reparsed = new Parser(
			lastIdentOrName.prettyPrint()
		).consumeReferenceType();
		if (!(reparsed instanceof ReferenceTypeTree)) return;

		resolveTypeTree(scope, reparsed, fillContext({}, 8));
		const ref = reparsed.referent;
		if (ref && (ref.type === "interface" || ref.type === "class")) {
			// We are on a reference type with type arguments
			view.dispatch([
				view.state.update({
					changes: {from: loc, to: loc, insert: ">"},
				}),
			]);
		}
	}

	onTypedGt(
		doc: string,
		before: CompilationUnitTree,
		after: CompilationUnitTree,
		loc: number,
		view: EditorView
	) {
		// Suppose we are in a reference type
		// ArrayList<Iterator<Integer|>>
		//                           ^
		// We have just pressed > and we wish to skip this input if all the angle brackets are already balanced. To do this,
		// we look at every parent reference type and check to see if their type arguments tree is not closed. If all
		// are closed, we skip the input.

		const chain = before.findChainAtPosition(loc - 1);
		if (!chain) return;

		const referenceTypes = chain.filter(
			(c) => c instanceof ReferenceTypeTree
		) as ReferenceTypeTree[];
		if (referenceTypes.length === 0) return;

		if (referenceTypes.every((rt) => !rt.typeArgs || rt.typeArgs?.isClosed())) {
			skipCharacter(view, loc);
		}
	}

	actionsExecuted = false;

	/**
	 * Given the state of the text before, and after, a transaction, the parsed state of the file before the
	 * transaction, and the location of the cursor after the transaction, execute "fancy actions" such as skipping
	 * certain inputs.
	 */
	executeFancyActions(
		before: string,
		after: string,
		parsedBefore: CompilationUnitTree,
		parsedAfter: CompilationUnitTree,
		loc: number,
		view: EditorView
	) {
		if (this.actionsExecuted) return;
		this.actionsExecuted = true;

		setTimeout(() => {
			this.actionsExecuted = false;
		}, 0); // prevent recursion

		const inferred = inferInsertionPoint(before, after, loc - 1);
		if (inferred?.index === loc - 1) {
			switch (inferred.inserted) {
				case "<":
					return this.onTypedLt(before, parsedBefore, parsedAfter, loc, view);
				case ">":
					return this.onTypedGt(before, parsedBefore, parsedAfter, loc, view);
			}
		}
	}
}
